import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Crown, Ticket, Flame } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const SEEN_KEY = "jayatk_ticket_prompt_seen";

export const WelcomeTicketModal = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [price, setPrice] = useState(10);
  const [mode, setMode] = useState("off");
  const [code, setCode] = useState("");
  const [codeOpen, setCodeOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SEEN_KEY)) return;
    let t;
    api.get("/site/status").then((r) => {
      const m = r.data.tickets_mode || "off";
      setMode(m);
      if (m === "off") return; // no ticket prompt while sales are closed
      const amt = (r.data.ep3_tier === "general" ? r.data.prices?.ep3_general : r.data.prices?.ep3_early);
      if (amt) setPrice(amt / 100);
      api.get("/tickets/availability?event=ep3-nov28").then((av) => {
        if (av.data.sales_enabled === false) return; // Ep3 sales open Friday midnight — no popup
        setRemaining(av.data.remaining);
        t = setTimeout(() => setOpen(true), 1100);
      }).catch(() => {});
    }).catch(() => {});
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    sessionStorage.setItem(SEEN_KEY, "1");
    setOpen(false);
  };

  const buy = async () => {
    if (mode === "early" && !code.trim()) {
      toast.error("Enter your early access code first — it's in your email or text.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
        product: "ep3",
        code: code.trim(),
      });
      sessionStorage.setItem(SEEN_KEY, "1");
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[70] flex items-center justify-center px-6 bg-black/75 backdrop-blur-md"
          onClick={close} data-testid="welcome-ticket-modal">
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md border border-[#D4AF37]/30 bg-[#0A0A0A] p-9 md:p-10 gold-glow overflow-hidden">
            <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#5C0000]/40 blur-3xl pointer-events-none" />

            <button onClick={close} data-testid="welcome-close"
              className="absolute top-5 right-5 text-[#A19D94] hover:text-[#D4AF37] transition-colors" aria-label="Close">
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-2 mb-6">
              <Crown className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">You're Invited</span>
            </div>

            <h2 className="font-serif-display text-4xl leading-[0.95] tracking-tight text-[#FDFBF7]">
              Chocolate <span className="italic text-gradient-gold">City</span>
            </h2>
            <p className="text-[#A19D94] mt-4 leading-relaxed">
              Episode Three · Grown & Sexy Edition · Saturday, Nov 28 · 5–10PM · St. Louis. Lock in your {"$"}{price} early bird ticket — shades of brown attire, candlelight, and nothing but R&B.
            </p>
            {remaining !== null && remaining > 0 && (
              <p className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#F4D77E]" data-testid="modal-remaining">
                <Flame className="w-3.5 h-3.5" /> {remaining} presale tickets left
              </p>
            )}

            <div className="mt-8 flex flex-col gap-3">
              {mode === "early" && (
                <div data-testid="welcome-early-code">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#F4D77E] mb-3">You're in the 24-hour early window — enter the code we sent you:</p>
                  <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="EARLY ACCESS CODE" data-testid="welcome-code-input"
                    className="w-full bg-transparent border border-[#D4AF37]/40 px-4 py-3 text-center text-[#FDFBF7] tracking-[0.25em] placeholder-[#5c5850] focus:outline-none focus:border-[#D4AF37] transition-colors" />
                </div>
              )}
              {mode === "public" && !codeOpen && (
                <button onClick={() => setCodeOpen(true)} data-testid="welcome-code-toggle"
                  className="text-[11px] uppercase tracking-[0.2em] text-[#8C7324] hover:text-[#D4AF37] transition-colors self-center">
                  Have a discount code?
                </button>
              )}
              {mode === "public" && codeOpen && (
                <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="DISCOUNT CODE (OPTIONAL)" data-testid="welcome-code-input"
                  className="w-full bg-transparent border border-white/20 px-4 py-3 text-center text-[#FDFBF7] tracking-[0.25em] placeholder-[#5c5850] focus:outline-none focus:border-[#D4AF37] transition-colors" />
              )}
              <button onClick={buy} disabled={loading} data-testid="welcome-buy-button"
                className="w-full inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.25em] border border-[#D4AF37] bg-[#D4AF37] text-black px-8 py-4 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                <Ticket className="w-4 h-4" />
                {loading ? "Redirecting…" : mode === "early" ? `Unlock $${price} Early Tickets` : `Get $${price} Presale`}
              </button>
              <button onClick={close} data-testid="welcome-explore-button"
                className="w-full text-xs uppercase tracking-[0.2em] text-[#A19D94] hover:text-[#FDFBF7] py-2 transition-colors duration-300">
                Explore the site first
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
